import * as React from 'react';
import './App.css';
import { Editor } from './editor/Editor';
import { Translation, tsxl, l } from './editor/client/Other/Translation';
import { reducer, reduxActions } from './reducer';
import { DocumentTypes } from './editor/client/Interfaces/Draft/DraftInterface';
import { defaultDocument, getCountry } from './editor/client/actions/helpers/draft';
import { getSettings } from './editor/staticData/settings';
import { settingsStub } from './editor/client/Interfaces/Settings/__stubs__/SettingsInterface.stubs';
import * as ReactDOM from 'react-dom';
import axios from 'axios';
import { Preview } from './editor/Preview';
import { getLocale } from './editor/client/Other/functions/queryStringFunctions';
import DataConsentBanner from './editor/client/components/DataConsentBanner';
import { getVATOptions } from './editor/staticData/vat';
import { getAppConfig } from './config';
import MobileMessage from './editor/client/components/MobileMessage';

const getLambdaBaseURL = (): string => {
  const CONF = getAppConfig();
  return CONF.lambdaURL;
};

const getTemplateUrl = () => `${getLambdaBaseURL()}/document/invoice/${getLocale()}`;
const getPDFUrl = () => `${getLambdaBaseURL()}/document/pdf`;

const loadLanguage = (locale) => {
  return Translation.load(locale);
};

const getPDF = async (html: string) =>
  axios.post(getPDFUrl(), { html }, { headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' } })
    .then((res) => res.data);

const getHtml = async (draft) => {
  return await axios.post(getTemplateUrl(), draft).then((resp) => resp.data);
};

const generateHTML = async (draft: any, setHtml: (html: string) => void) => {
  setHtml(await getHtml(draft));
};

const getFileName = (draft): string => {
  const { summary, fields } = draft;
  const { currency = 'EUR', grossTotal = 0 } = summary;
  const invoiceNumber = fields.invoiceNumber.value || '';
  const numSpace = invoiceNumber === '' ? '' : ` ${invoiceNumber}`;
  const docType = l('invoice.DefaultTitle');
  return `${docType}${numSpace} - ${grossTotal} ${currency}.pdf`;
};

const downloadPDF = async (draft, html) => {
  if (!html) {
    html = await getHtml(draft);
  }
  const data = await getPDF(html);
  const linkSource = `data:application/pdf;base64,${data}`;
  const downloadLink = document.createElement('a');
  const fileName = getFileName(draft);
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
  return true;
};

const openPdfWindow = async (draft, html) => {
  if (!html) {
    html = await getHtml(draft);
  }
  const w = window.open();
  w.document.write(html);
  w.window.print();
  w.document.close();
  w.window.close();
};

const isMobileSize = () => window.innerWidth <= 750;

const App: React.FC = () => {
  const [vitalsLoaded, setVitalsLoaded] = React.useState(false);
  const [loadingPdf, setLoadingPdf] = React.useState(false);
  const [locale] = React.useState(getLocale());
  const settings = {
    ...getSettings(locale),
    managedFields: { ...settingsStub.managedFields, VAT: getVATOptions(getCountry(locale)) }
  };
  const draft = defaultDocument(DocumentTypes.INVOICE, settings, locale);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [state, dispatch] = React.useReducer(reducer, { draft });
  const [html, setHtml] = React.useState<string>(null);
  const handleDownloadPdf = async (draft, html) => {
    if (loadingPdf) {
      return false;
    }
    setLoadingPdf(true);
    return downloadPDF(draft, html).finally(() => setLoadingPdf(false));
  };
  const actions = reduxActions(dispatch);

  React.useEffect(() => {
    Promise.all([loadLanguage(locale)]).then(() => setVitalsLoaded(true));
  });

  if (isMobileSize()) {
    return <MobileMessage language={getCountry(locale).toLowerCase()} />;
  }

  return (
    <>
      {ReactDOM.createPortal(
        tsxl('zervant.external.editor.title'),
        window.document.getElementsByTagName('title')[0]
      )}
      {vitalsLoaded && (
        <div className="App">
          <div>
            <Editor
              draft={state.draft}
              actions={actions}
              previewDraft={(draft) => generateHTML(draft, setHtml)}
              openPdfWindow={(draft) => openPdfWindow(draft, null)}
              downloadPDF={async (draft) => handleDownloadPdf(draft, null)}
              settings={settings}
              locale={locale}
              language={getCountry(locale).toLowerCase()}
            />
            <Preview
              html={html}
              isOpen={Boolean(html)}
              close={() => setHtml(null)}
              openPdfWindow={() => openPdfWindow(state.draft, html)}
              downloadPDF={async () => handleDownloadPdf(state.draft, html)}
            />
            <DataConsentBanner />
          </div>
        </div>
      )}
    </>
  );
};

export default App;
